import { StatusCodes } from "http-status-codes";
import {
  IAllCompleteHealthCare,
  IAllUsersHealthCaresResponse,
  IBillingUrlFormResponse,
  IDepartment,
  IDepartmentResponse,
  IDesignation,
  IDesignationResponse,
  IFetchAllBillingUrlFormResponse,
  IFetchAllHealthCareDoctorResponse,
  IFetchAllHealthCareSchemeResponse,
  IFetchAllSchemeSpecificLoginDetailsResponse,
  IFetchManageUserResponse,
  IHealthCareDoctorForm,
  IHealthCareScheme,
  IHealthCareSchemeForm,
  IManageUserForm,
  IPortal,
  IPortalResponse,
  IQualification,
  IQualificationResponse,
  ISchemeSpecificLoginDetailsForm,
  IUserHealthCares,
} from "../../interfaces/IHealthCare";
import Axios from "../../utils/axiosInterceptor";
import { ALL_SCHEMES_APIS, HEALTH_CARE_APIS } from "../../utils/constants";

export const fetchHealthCareById = async (
  id: number
): Promise<IAllCompleteHealthCare> => {
  try {
    const response = await Axios.get(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${id}`
    );

    if (response.status === StatusCodes.OK) {
      return {
        healthCareRes: response.data.healthCare,
        healthCareRequiredDocumentsRes:
          response.data.healthCareRequiredDocuments,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch health care details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching health care details:", error);
    throw new Error("Cannot fetch health care details");
  }
};

export const fetchAllHealthCareScheme = async (
  healthCareId: number
): Promise<IFetchAllHealthCareSchemeResponse> => {
  try {
    const response = await Axios.get<IHealthCareScheme[]>(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_schemes`
    );

    if (response.status === StatusCodes.OK) {
      return { schemeRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot fetch health care schemes",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching health care schemes:", error);
    throw new Error("Cannot fetch health care schemes");
  }
};

export const fetchAllHealthCareDoctor = async (
  healthCareId: number
): Promise<IFetchAllHealthCareDoctorResponse> => {
  try {
    const response = await Axios.get<IHealthCareDoctorForm[]>(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_doctors`
    );

    if (response.status === StatusCodes.OK) {
      return { doctorRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot fetch health care doctors",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching health care doctors:", error);
    throw new Error("Cannot fetch health care doctors");
  }
};

export const fetchAllManageUsers = async (
  healthCareId: number
): Promise<IFetchManageUserResponse> => {
  try {
    const response = await Axios.get<IManageUserForm[]>(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_management_user`
    );

    if (response.status === StatusCodes.OK) {
      return { manageUserRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot fetch manage users",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching manage users:", error);
    throw new Error("Cannot fetch manage users");
  }
};

// {
//   schemeId: number;
//   hospitalId: number;
//   portalId: number;
// }

export const fetchPortalUserDetails = async (payload: any) => {
  const response = await Axios.post(
    HEALTH_CARE_APIS.GET_PORTAL_USER_DETAILS,
    payload
  );
  return response?.data;
};

export const fetchAllSchemeSpecificLoginDetails = async (
  healthCareId: number
): Promise<IFetchAllSchemeSpecificLoginDetailsResponse> => {
  try {
    const response = await Axios.get<ISchemeSpecificLoginDetailsForm[]>(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_mis_credentials_scheme_specific`
    );

    if (response.status === StatusCodes.OK) {
      return {
        schemeSpecificLoginDetailsRes: response.data,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch scheme specific login details",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching scheme specific login details", error);
    throw new Error("Cannot fetch scheme specific login details");
  }
};

export const fetchAllBillingUrlForm = async (
  healthCareId: number
): Promise<IFetchAllBillingUrlFormResponse> => {
  try {
    const response = await Axios.get<IBillingUrlFormResponse[]>(
      `${HEALTH_CARE_APIS.ADD_HEALTHCARE}/${healthCareId}/health_care_billing_credentials`
    );

    if (response.status === StatusCodes.OK) {
      return {
        billingUrlFormRes: response.data,
        status: response.status,
      };
    } else {
      return {
        errorMessage: "Cannot fetch billing url form",
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Error fetching billing url form", error);
    throw new Error("Cannot fetch billing url form");
  }
};

export const fetchAllPortals = async (): Promise<IPortalResponse> => {
  try {
    const response = await Axios.get<Array<IPortal>>(
      `${HEALTH_CARE_APIS.GET_PORTALS}`
    );
    if (response.status === StatusCodes.OK) {
      return { portals: response.data };
    } else {
      return {
        errorMessage: "Cannot fetch all portals",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot fetch all portals");
  }
};

export const fetchAllDepartments = async (): Promise<IDepartmentResponse> => {
  try {
    const response = await Axios.get<Array<IDepartment>>(
      `${HEALTH_CARE_APIS.GET_DEPARTMENTS}`
    );
    if (response.status === StatusCodes.OK) {
      return { departments: response.data };
    } else {
      return {
        errorMessage: "Cannot fetch all departments",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot fetch all departments");
  }
};

export const fetchAllDesignation = async (): Promise<IDesignationResponse> => {
  try {
    const response = await Axios.get<Array<IDesignation>>(
      `${HEALTH_CARE_APIS.GET_DESIGNATIONS}`
    );
    if (response.status === StatusCodes.OK) {
      return { designation: response.data };
    } else {
      return {
        errorMessage: "Cannot fetch all designations",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot fetch all designations");
  }
};

export const fetchAllQualification =
  async (): Promise<IQualificationResponse> => {
    try {
      const response = await Axios.get<Array<IQualification>>(
        `${HEALTH_CARE_APIS.GET_QUALIFICATIONS}`
      );
      if (response.status === StatusCodes.OK) {
        return { qualification: response.data };
      } else {
        return {
          errorMessage: "Cannot fetch all qualifications",
          status: response.status,
        };
      }
    } catch (error) {
      throw new Error("Cannot fetch all qualifications");
    }
  };

export const fetchAllUsersHealthCares =
  async (): Promise<IAllUsersHealthCaresResponse> => {
    try {
      const response = await Axios.get<Array<IUserHealthCares>>(
        `${HEALTH_CARE_APIS.GET_ALLUSERS_HEALTHCARES}`
      );
      if (response.status === StatusCodes.OK) {
        return { users: response.data };
      } else {
        return {
          errorMessage: "Cannot fetch all users health cares",
          status: response.status,
        };
      }
    } catch (error) {
      throw new Error("Cannot fetch all users health cares");
    }
  };
